import React from "react";

export default function UserStatusCard({
  theme = "dark",
  subscriptionLoading,
  hasPaidAccess,
  hasUploadAccess,
  hasPremiumAccess,
  isAdminBypass,
  isInternalTeam,
  isAdmin,
  freeUploadsRemaining,
  subscription,
}) {
  const isDark = theme === "dark";

  const colors = {
    cardBg: isDark ? "#0f172a" : "#ffffff",
    border: isDark ? "#334155" : "#d1d5db",
    text: isDark ? "#f8fafc" : "#111827",
    muted: isDark ? "#cbd5e1" : "#6b7280",
    goodBg: isDark ? "rgba(16,185,129,0.16)" : "#ecfdf5",
    goodBorder: "#10b981",
    warnBg: isDark ? "rgba(245,158,11,0.16)" : "#fffbeb",
    warnBorder: "#f59e0b",
    badBg: isDark ? "rgba(239,68,68,0.14)" : "#fef2f2",
    badBorder: "#ef4444",
    blueBg: isDark ? "rgba(37,99,235,0.18)" : "#eff6ff",
    blueBorder: "#2563eb",
  };

  let title = "Checking access...";
  let subtitle = "Please wait while we verify your account access.";
  let tag = "LOADING";
  let bg = colors.blueBg;
  let border = colors.blueBorder;

  if (!subscriptionLoading) {
    if (isAdmin) {
      title = "Master Admin Access Enabled";
      subtitle = "You can manage users, internal team access, leads, dashboards, reports, uploads, exports, and settings.";
      tag = "ADMIN";
      bg = colors.blueBg;
      border = colors.blueBorder;
    } else if (isInternalTeam || isAdminBypass) {
      title = "Internal Team Access Enabled";
      subtitle = "You have full dashboard access without payment. You can upload, save reports, export PDF, and print reports.";
      tag = "INTERNAL TEAM";
      bg = colors.goodBg;
      border = colors.goodBorder;
    } else if (hasPaidAccess) {
      const dateText = subscription?.expiryDate
        ? new Date(subscription.expiryDate).toLocaleDateString()
        : "active";
      title = "Subscription Active";
      subtitle = `${subscription?.provider || "Payment"} / ${subscription?.plan || "starter"} plan is active. Valid till ${dateText}.`;
      tag = "PAID";
      bg = colors.goodBg;
      border = colors.goodBorder;
    } else if (hasUploadAccess && Number(freeUploadsRemaining || 0) > 0) {
      title = "Free Trial Available";
      subtitle = `You have ${freeUploadsRemaining} free upload remaining. Demo dashboards are always available. Upgrade for export, print, and saved reports.`;
      tag = "FREE TRIAL";
      bg = colors.warnBg;
      border = colors.warnBorder;
    } else {
      title = "Trial Used — Upgrade Required";
      subtitle = "Demo dashboards are still available, but upload, save, export, and print need a paid plan.";
      tag = "UPGRADE";
      bg = colors.badBg;
      border = colors.badBorder;
    }
  }

  return (
    <div
      style={{
        marginTop: 18,
        marginBottom: 18,
        padding: 18,
        borderRadius: 18,
        border: `1px solid ${border}`,
        background: bg,
        color: colors.text,
        boxShadow: isDark ? "0 14px 35px rgba(0,0,0,0.25)" : "0 14px 35px rgba(15,23,42,0.08)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 6 }}>{title}</div>
          <div style={{ color: colors.muted, lineHeight: 1.55 }}>{subtitle}</div>
        </div>

        <div
          style={{
            alignSelf: "flex-start",
            padding: "8px 12px",
            borderRadius: 999,
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            color: colors.text,
            fontSize: 12,
            fontWeight: 900,
            letterSpacing: "0.05em",
          }}
        >
          {tag}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 10,
          marginTop: 16,
        }}
      >
        <SmallMetric label="Upload" value={hasUploadAccess ? "Allowed" : "Locked"} />
        <SmallMetric label="Save Reports" value={hasPremiumAccess ? "Allowed" : "Premium"} />
        <SmallMetric label="Export / Print" value={hasPremiumAccess ? "Allowed" : "Premium"} />
        <SmallMetric label="Free Uploads" value={isAdminBypass ? "Unlimited" : String(freeUploadsRemaining || 0)} />
      </div>
    </div>
  );
}

function SmallMetric({ label, value }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 14,
        background: "rgba(255,255,255,0.12)",
        border: "1px solid rgba(148,163,184,0.25)",
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.78, fontWeight: 800 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 900, marginTop: 4 }}>{value}</div>
    </div>
  );
}
